// Persistent in-call bar — the softphone's controls, on every page.
//
// WHY: the SIP session now survives navigation (see SipContext.tsx), but the
// controls did not. An agent watching Real-time Call Logs mid-call had no way to
// mute, transfer or even hang up without navigating back to the Softphone page —
// and "transfer doesn't work" turned out to mean "the Transfer button isn't on
// this screen". A live desk needs the controls wherever the agent is, because
// the whole point of surviving navigation is to look things up while talking.
//
// Renders nothing unless a call is up, so it costs nothing on every other page.
//
// Look: the template's card surface and buttons (no live call UI in the
// template; the footer row of its patient-monitoring screen). It renders
// outside .helix, so it reads only :root tokens and template parts (HxButton
// is the template's Button and needs no .helix). Record:
// docs/testing/ui-migration/075-call-bar.md.

import { useEffect, useRef, useState } from "react";
import { Phone, PhoneOff, Mic, MicOff, Pause, ArrowRightLeft, Users } from "lucide-react";
import { useSoftphone } from "./SipContext";
import AgentPick from "./AgentPick";

import { HxButton, HxChip, HxInput } from "../../components/hx";
import { cn } from "../../lib/utils";

function useTalkTimer(active: boolean): string {
  const [secs, setSecs] = useState(0);
  const ref = useRef<number | null>(null);
  useEffect(() => {
    if (!active) { setSecs(0); return; }
    ref.current = window.setInterval(() => setSecs((s) => s + 1), 1000);
    return () => { if (ref.current) window.clearInterval(ref.current); };
  }, [active]);
  return `${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`;
}

// Both strips. pointer-events-auto keeps the bar clickable while a template
// dialog or sheet is open (Radix turns pointer events off on the rest of the
// page), and data-callbar on the element tells the dialog a click here is not
// a click outside (components/ui/dialog.tsx, passes 025 and 036). z 900 sits
// above every template overlay (z-50); only the incoming-call dialog is higher.
const STRIP =
  "pointer-events-auto fixed inset-x-0 bottom-0 z-[900] flex flex-wrap items-center gap-3 border-t bg-card px-4 py-2.5 text-card-foreground shadow-[0_-6px_24px_color-mix(in_srgb,var(--ink)_18%,transparent)]";

// The status dot, with a 22% halo of its own colour.
function Dot({ tone }: { tone: "ok" | "warn" }) {
  const c = `var(--${tone})`;
  return (
    <span
      aria-hidden="true"
      className="size-2.5 flex-none rounded-full"
      style={{ background: c, boxShadow: `0 0 0 3px color-mix(in srgb, ${c} 22%, transparent)` }}
    />
  );
}

export default function CallBar() {
  const sip = useSoftphone();
  const live = sip.status === "oncall";
  const ringingOut = !!sip.call && !sip.incoming && !live;
  const talk = useTalkTimer(live);
  const [xfer, setXfer] = useState(false);
  const [target, setTarget] = useState("");
  // "blind" hands the caller over immediately; "warm" rings the colleague first,
  // with the caller on hold, so the agent can explain before connecting them.
  const [mode, setMode] = useState<"warm" | "blind">("warm");

  // Close the transfer field when the call goes away, so it isn't still open on
  // the next call.
  useEffect(() => { if (!sip.call) { setXfer(false); setTarget(""); } }, [sip.call]);

  // AN INCOMING CALL MUST BE ANSWERABLE FROM ANY PAGE.
  //
  // The ringing card lived only on the softphone desk, and `sip.call` is null
  // until the call is answered — so this bar rendered nothing while the phone
  // was ringing. A supervisor who pressed Listen on Agents & states heard their
  // own phone ring with no way on screen to pick it up, and the monitor leg
  // rang out every single time; the switch's log showed "PJSIP/3000 is ringing"
  // and then the channel simply vanishing. G3 looked broken for four rounds of
  // testing because of a missing button on the page the feature is used from.
  //
  // The same gap applied to ordinary calls: an agent reading a conversation
  // could not answer their phone without navigating back to the desk first.
  if (sip.incoming && !sip.call) {
    const inc = sip.incoming;
    const monitorLeg = /^Monitor\b/i.test(inc.name ?? "");
    return (
      <div role="region" aria-label="Incoming call" data-callbar="" className={STRIP}>
        <Dot tone="warn" />
        <span className="text-sm font-semibold">
          {monitorLeg ? "Monitoring session" : (inc.name ?? "Incoming call")}
        </span>
        <span className="font-mono text-xs text-muted-foreground">{inc.number}</span>
        <span className="text-xs text-muted-foreground">
          {monitorLeg ? "answer to hear the call" : "ringing"}
        </span>
        <span className="flex-1" />
        {/* Answer is the one filled button and Decline a quiet outline, as in
            the incoming-call dialog (VoiceSoftphone.tsx, IncomingCall): two
            equal buttons got Decline clicked by mistake. Decided 6 Oct 2026. */}
        <HxButton variant="primary" size="sm" onClick={() => void sip.answer()}>
          <Phone /> Answer
        </HxButton>
        <HxButton size="sm" onClick={sip.reject}
          className="border-destructive bg-transparent text-destructive hover:bg-destructive/10 hover:text-destructive dark:border-destructive dark:bg-transparent dark:hover:bg-destructive/10">
          <PhoneOff /> Decline
        </HxButton>
      </div>
    );
  }

  if (!sip.call) return null;

  const doTransfer = () => {
    const t = target.trim();
    if (!t) return;
    if (mode === "blind") sip.transfer(t);
    else void sip.startConsult(t);
    setXfer(false);
    setTarget("");
  };

  return (
    <div role="region" aria-label="Active call" data-callbar="" className={STRIP}>
      <Dot tone={live ? "ok" : "warn"} />
      <span className="text-sm font-semibold">{sip.call.name ?? "Caller"}</span>
      <span className="font-mono text-xs text-muted-foreground">{sip.call.number}</span>
      <HxChip>{sip.call.direction === "in" ? "inbound" : "outbound"}</HxChip>
      <span className={cn("min-w-13 font-mono text-sm font-semibold tabular-nums", live ? "text-foreground" : "text-muted-foreground")}>
        {live ? talk : ringingOut ? "ringing" : "…"}
      </span>

      <span className="flex-1" />

      {/* A consultation is up: the caller is on hold and the agent is talking to
          the colleague. These three are the only actions that make sense here,
          so they replace the normal controls rather than sitting beside them. */}
      {sip.consult ? (
        <>
          <span className="text-xs text-muted-foreground">
            {sip.consultLive ? "Talking to" : "Ringing"}{" "}
            <strong className="font-mono text-foreground">{sip.consult.number}</strong>
            {" · caller on hold"}
          </span>
          {/* Both need the colleague to have ANSWERED — REFER-with-Replaces has
              nothing to replace until then, and an unanswered leg cannot join a
              conference. */}
          <HxButton variant="primary" size="sm" disabled={!sip.consultLive}
            onClick={sip.completeTransfer}
            title={sip.consultLive ? "Connect them and drop out" : "Waiting for them to answer"}>
            <ArrowRightLeft /> Complete
          </HxButton>
          <HxButton size="sm" disabled={!sip.consultLive}
            onClick={() => void sip.conference()}
            title={sip.consultLive ? "Put all three on one call" : "Waiting for them to answer"}>
            <Users /> Conference
          </HxButton>
          <HxButton variant="ghost" size="sm" onClick={() => void sip.cancelConsult()}
            title="Hang up on them and go back to the caller">
            Back to caller
          </HxButton>
        </>
      ) : xfer ? (
        <>
          {/* Colleagues by name, one click — the same list as the softphone
              card's form. Typing stays available for external numbers. */}
          <AgentPick excludeExt={sip.config?.ext} onPick={setTarget} />
          <div className="inline-flex overflow-hidden rounded-lg border">
            {(["warm", "blind"] as const).map((m) => (
              <HxButton key={m} size="sm" variant={mode === m ? "primary" : "ghost"} onClick={() => setMode(m)}
                aria-pressed={mode === m}
                title={m === "warm"
                  ? "Speak to them first, with the caller on hold"
                  : "Hand the caller over immediately, without speaking to them"}
                className={cn("rounded-none", mode !== m && "text-muted-foreground")}>
                {m === "warm" ? "Warm" : "Blind"}
              </HxButton>
            ))}
          </div>
          <HxInput
            autoFocus
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") doTransfer(); if (e.key === "Escape") setXfer(false); }}
            placeholder="Extension or number…"
            className="h-7 w-50"
          />
          <HxButton variant="primary" size="sm" disabled={!target.trim()} onClick={doTransfer}>
            {mode === "warm" ? "Call them" : "Transfer"}
          </HxButton>
          <HxButton variant="ghost" size="sm" onClick={() => setXfer(false)}>Cancel</HxButton>
        </>
      ) : (
        <>
          <HxButton size="sm" onClick={() => sip.setMute(!sip.muted)} title={sip.muted ? "Unmute" : "Mute"}>
            {sip.muted ? <MicOff /> : <Mic />} {sip.muted ? "Muted" : "Mute"}
          </HxButton>
          <HxButton size="sm" onClick={() => sip.setHold(!sip.held)} title={sip.held ? "Resume" : "Hold"}>
            <Pause /> {sip.held ? "Held" : "Hold"}
          </HxButton>
          {/* Only once connected — REFER on a still-ringing leg is rejected. */}
          <HxButton size="sm" disabled={!live} onClick={() => setXfer(true)}
            title={live ? "Transfer this caller (warm or blind)" : "Available once the call connects"}>
            <ArrowRightLeft /> Transfer
          </HxButton>
          <HxButton variant="ghost" size="sm" to="/voice/softphone" title="Open the full softphone">
            <Phone /> Softphone
          </HxButton>
          <HxButton variant="danger" solid size="sm" onClick={sip.hangup}>
            <PhoneOff /> End
          </HxButton>
        </>
      )}
    </div>
  );
}
